import { storageUrl } from '../services/api';
import { formatVariationShareText } from './productComposite';

/**
 * Pure Fisher-Yates shuffle that returns a new array with randomized elements
 * without mutating the original input array.
 *
 * @template T
 * @param {T[]} array
 * @returns {T[]}
 */
export function shuffleArray(array) {
  if (!Array.isArray(array) || array.length <= 1) {
    return Array.isArray(array) ? [...array] : [];
  }

  const result = [...array];
  for (let i = result.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [result[i], result[j]] = [result[j], result[i]];
  }
  return result;
}

/**
 * Calculate an organic, human-like delay with a 10%–20% random jitter margin
 * around base interval milliseconds to prevent automated bot/scheduler detection.
 *
 * Works universally for preset seconds (3s, 5s, 10s, 15s) and custom user seconds.
 * Margin range: -10% to +20% (multiplier 0.90 to 1.20).
 * Minimum floor: 1500ms (to ensure network socket stability).
 *
 * @param {number} baseMs - Base interval in milliseconds
 * @param {number} [minMargin=0.10] - Minimum negative margin (e.g. 0.10 for -10%)
 * @param {number} [maxMargin=0.20] - Maximum positive margin (e.g. 0.20 for +20%)
 * @returns {number} Delay in milliseconds
 */
export function getOrganicInterval(baseMs, minMargin = 0.10, maxMargin = 0.20) {
  const safeBase = Number.isFinite(baseMs) && baseMs > 0 ? baseMs : 5000;

  // Jitter factor in range [-minMargin, +maxMargin]
  const jitterFactor = Math.random() * (maxMargin + minMargin) - minMargin;
  const organicMs = Math.round(safeBase * (1 + jitterFactor));

  // If baseMs is a test/micro-interval (<= 1000ms), respect the scale (e.g. 50ms stays fast for tests).
  // For production intervals (>= 1500ms), enforce a safety floor of 1500ms.
  if (safeBase <= 1000) {
    return Math.max(10, organicMs);
  }

  return Math.max(1500, organicMs);
}

/**
 * Return formatted min and max seconds for UI helper hints (e.g. custom seconds preview).
 *
 * @param {number} baseSeconds - Base interval in seconds
 * @param {number} [minMargin=0.10]
 * @param {number} [maxMargin=0.20]
 * @returns {{ minSeconds: number, maxSeconds: number, label: string }}
 */
export function getOrganicIntervalRange(baseSeconds, minMargin = 0.10, maxMargin = 0.20) {
  const safeSec = Number.isFinite(baseSeconds) && baseSeconds > 0 ? baseSeconds : 5;
  const min = Math.max(1.5, Math.round(safeSec * (1 - minMargin) * 10) / 10);
  const max = Math.round(safeSec * (1 + maxMargin) * 10) / 10;

  return {
    minSeconds: min,
    maxSeconds: max,
    label: `~${min}s – ${max}s`,
  };
}

/** Interval choices shared by the WhatsApp send modals (seconds, or 'custom'). */
export const INTERVAL_PRESETS = [
  { value: '3', label: '3 detik (Cepat)' },
  { value: '5', label: '5 detik (Standar - Disarankan)' },
  { value: '10', label: '10 detik (Santai)' },
  { value: '15', label: '15 detik (Sangat Aman)' },
  { value: 'custom', label: 'Kustom (Tentukan detik)' },
];

/** Longest gap Libericano accepts between broadcast messages (throttle_min/max_seconds 0–3600). */
export const MAX_THROTTLE_SECONDS = 3600;

/**
 * Whole-second pacing window for the gateway, which picks a random delay in
 * [min, max] between messages. Same -10%/+20% organic margin as the browser queue,
 * with the upper bound capped at the gateway limit.
 *
 * @param {number} baseSeconds - Base interval in seconds
 * @returns {{ min: number, max: number }}
 */
export function getThrottleWindow(baseSeconds) {
  const { minSeconds, maxSeconds } = getOrganicIntervalRange(baseSeconds);
  return {
    min: Math.max(2, Math.floor(minSeconds)),
    max: Math.min(MAX_THROTTLE_SECONDS, Math.ceil(maxSeconds)),
  };
}

/** More recipients than this are sent in batches, one gateway broadcast per batch (backend: CataloguePdfRunService). */
export const BATCH_THRESHOLD = 15;

export const MAX_BATCH_SIZE = 12;

/** Longest cooldown between batches the backend accepts. */
export const MAX_COOLDOWN_MINUTES = 60;

/** Cooldown choices between batches (minutes, or 'custom'). */
export const COOLDOWN_PRESETS = [
  { value: '5', label: '5–6 menit (Standar - Disarankan)' },
  { value: '10', label: '10–12 menit (Lebih Aman)' },
  { value: '15', label: '15–18 menit (Sangat Aman)' },
  { value: 'custom', label: 'Kustom (Tentukan menit)' },
];

/**
 * Random cooldown window after each batch, in seconds: the base up to +20% (5 min → 300–360s),
 * the upper bound capped at the backend limit.
 *
 * @param {number} minutes - Base cooldown in minutes
 * @returns {{ min: number, max: number }}
 */
export function getCooldownWindow(minutes) {
  const min = Math.round(minutes * 60);
  return { min, max: Math.min(MAX_COOLDOWN_MINUTES * 60, Math.round(min * 1.2)) };
}

/**
 * Split a list into the fewest batches of at most `maxSize`, as even as possible,
 * so no batch is left tiny (16 → 8 + 8, 37 → 10 + 9 + 9 + 9).
 *
 * @template T
 * @param {T[]} list
 * @param {number} [maxSize]
 * @returns {T[][]}
 */
export function splitIntoBatches(list, maxSize = MAX_BATCH_SIZE) {
  if (!list.length) return [];
  const count = Math.ceil(list.length / maxSize);
  const base = Math.floor(list.length / count);
  const extra = list.length % count;

  const batches = [];
  let start = 0;
  for (let i = 0; i < count; i++) {
    const size = base + (i < extra ? 1 : 0);
    batches.push(list.slice(start, start + size));
    start += size;
  }
  return batches;
}

/**
 * Build the Cartesian queue items for a WhatsApp broadcast with full entropy:
 * 1. Shuffles recipients so delivery order is non-deterministic.
 * 2. Shuffles products independently for each recipient.
 * 3. Randomly picks 1 of the 3 alternative copy tones per product.
 *
 * @param {Object} options
 * @param {Array} options.products - Array of { item, drafts }
 * @param {Array} options.recipients - Array of recipient objects
 * @param {Function} [options.skip] - (recipient, item) => true to leave that pair out
 * @returns {Array<Object>} Formatted queue task items
 */
export function buildOrganicBroadcastQueue({ products = [], recipients = [], skip }) {
  if (!products.length || !recipients.length) return [];

  const rawTasks = [];

  for (const recipient of recipients) {
    for (const prod of products) {
      if (skip?.(recipient, prod.item)) continue;
      // Randomly select 1 of the 3 alternative drafts (Promotif / Kasual / Profesional)
      const drafts = prod.drafts || [];
      const randomIndex = drafts.length > 0 ? Math.floor(Math.random() * drafts.length) : 0;
      const pickedDraft = drafts[randomIndex];
      const toneLabel = pickedDraft?.tone || `Variasi #${randomIndex + 1}`;
      // Rebuilt from the description rather than the saved full_text, so drafts saved
      // earlier still get the current price and the product link.
      const messageText = formatVariationShareText(pickedDraft?.description, prod.item);

      // Resolve first non-video picture URL
      const rawPicPath =
        prod.item?.pictures?.find(
          (p) => p?.pict_url && !p.pict_url.match(/\.(mp4|webm|mov|avi)(\?|$)/i)
        )?.pict_url ||
        prod.item?.pictures?.[0]?.pict_url ||
        prod.item?.picture ||
        prod.item?.pict_url ||
        null;
      const mediaUrl = rawPicPath ? storageUrl(rawPicPath) : null;

      rawTasks.push({
        recipient,
        product: prod.item,
        draft: pickedDraft,
        tone: toneLabel,
        messageText,
        mediaUrl,
        status: 'pending', // 'pending' | 'active' | 'success' | 'error'
        error: null,
        timestamp: null,
      });
    }
  }

  // Fully scramble all (recipient, product) pairs across the entire queue
  // e.g. [rec1-prod2, rec2-prod1, rec2-prod3, rec1-prod3, ...]
  const scrambledTasks = shuffleArray(rawTasks);

  return scrambledTasks.map((task, idx) => ({
    ...task,
    id: `task_${idx + 1}_${Date.now()}_${Math.random().toString(36).substr(2, 4)}`,
    index: idx + 1,
  }));
}
